/*
 * THE ADDRESS'S STATE AND WHAT CAN BE DONE ABOUT IT (stage 4b phase D,
 * decisions 605, 608, 610). Shared by Settings → Account and the pending screen,
 * which is the point of it being a component: the person most likely to need to
 * fix a mistyped address is the one waiting for approval at it (decision 610).
 *
 * THE STATES, READ FROM THE SERVER, NEVER RE-DERIVED:
 *
 *   - `email_status` folds the two address timestamps into verified /
 *     unverified / bounced, with a bounce outranking a verification.
 *   - `pending_email` is a change of address in flight: the account still has
 *     its old address, and the new one waits for its link. While it is present
 *     the controls are about THAT (re-send its link, or cancel it), because the
 *     new address arrives verified and re-verifying the old one would be beside
 *     the point.
 *
 * REAL BUTTONS (Kevin's review). Every control here DOES something -- none of
 * them navigates -- so each is a bordered button rather than text that could
 * be read as a link. On the pending screen they stack with room between them
 * so a thumb cannot land on the wrong one; on Settings they are the size of
 * their words, at the start and end of the row.
 *
 * The current password is asked for only when the account has one
 * (`has_password`): a passkey-only or OIDC-only account has nothing to type.
 */

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, api, type User } from "../lib/api";
import styles from "./EmailAddress.module.css";

export interface EmailAddressProps {
  user: User;
  /**
   * Which screen this sits on. The pending screen's re-send reads "Re-send
   * email" and its buttons stack; Settings names the verification it re-sends
   * and lays its buttons in a row.
   */
  variant: "settings" | "pending";
  onUserChanged: (user: User) => void;
}

/**
 * The word beside the address itself. Exported so each screen places it next
 * to the address it already renders.
 *
 *   - "ERROR" when mail to it is bouncing, in amber rather than the error red:
 *     nothing the reader did caused it, and red in this app is the colour of a
 *     failed action.
 *   - "(Verified)" when `showVerified` and the address is proven -- Settings
 *     only, on the address's own line rather than a line of its own (Kevin's
 *     review). The pending screen has no use for it: its box disappears once
 *     the address is verified.
 */
export function AddressFlag({ user, showVerified = false }: { user: User; showVerified?: boolean }) {
  const { t } = useTranslation();
  if (user.email_status === "bounced") {
    return <strong className={styles.flag}>{t("email.bouncedFlag")}</strong>;
  }
  if (showVerified && user.email_status === "verified") {
    return <span className={styles.verifiedFlag}>{t("email.verifiedFlag")}</span>;
  }
  return null;
}

export function EmailAddress({ user, variant, onUserChanged }: EmailAddressProps) {
  const { t } = useTranslation();

  const [busy, setBusy] = useState(false);
  // One line of news at a time: the outcome of the last thing pressed.
  const [message, setMessage] = useState<string | null>(null);

  const [changing, setChanging] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [changeError, setChangeError] = useState<string | null>(null);

  const status = user.email_status;
  const pending = user.pending_email ?? null;
  // An account made before verification existed was never sent a link, so it
  // is offered one to SEND rather than told one is waiting (Kevin's review).
  const neverSent = !user.email_verification_sent;
  // Stacked full-width on the pending screen's narrow card, where a thumb is
  // the pointer; a row of text-sized buttons on Settings, start and end.
  const buttons = variant === "settings" ? styles.row : styles.stack;

  /** Re-read the account, so `pending_email` reflects what was just done. */
  async function refresh() {
    const me = await api.me().catch(() => null);
    if (me?.user) onUserChanged(me.user);
  }

  /** Run one action and turn its failure into our sentence. */
  async function run(action: EmailAction) {
    setBusy(true);
    setMessage(null);
    try {
      setMessage(await action());
    } catch (err) {
      setMessage(statusOf(err) === 429 ? t("email.cooldown") : t("email.sendFailed"));
    } finally {
      setBusy(false);
    }
  }

  const resendVerification = () =>
    run(async () => {
      await api.requestEmailVerification();
      if (neverSent) {
        // The first link: re-read the account so the note and the button move to
        // the "a link has been sent" wording, which then says it on its own.
        await refresh();
        return "";
      }
      return t("email.verificationSent", { email: user.email });
    });

  const resendChange = () =>
    run(async () => {
      await api.resendEmailChange();
      return t("email.verificationSent", { email: pending ?? "" });
    });

  const cancelChange = () =>
    run(async () => {
      const body = await api.cancelEmailChange();
      onUserChanged(body.user as User);
      return "";
    });

  async function submitChange(e: FormEvent) {
    e.preventDefault();
    setChangeError(null);

    const target = newEmail.trim().toLowerCase();
    // Refused here rather than by the server's 400, because the server's 400
    // also means "wrong password" and the two need different sentences.
    if (target === user.email.toLowerCase()) {
      setChangeError(t("email.changeSame"));
      return;
    }

    setBusy(true);
    try {
      await api.requestEmailChange(target, user.has_password ? currentPassword : undefined);
      setChanging(false);
      setNewEmail("");
      setCurrentPassword("");
      setMessage(t("email.changeSent", { email: target }));
      await refresh();
    } catch (err) {
      const s = statusOf(err);
      setChangeError(
        s === 400
          ? t("email.changeWrongPassword")
          : s === 429
            ? t("email.cooldown")
            : t("email.changeFailed"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.block}>
      {/* A change in flight: the address the account will move to, once its
          link is opened. */}
      {pending ? (
        <p className={styles.pending}>
          <span className={styles.pendingLabel}>{t("email.pendingLabel")}</span>
          <span className={styles.pendingAddress}>{pending}</span>
        </p>
      ) : null}

      {/* The state, in words. ONE amber line on a bounce. */}
      {status === "bounced" ? (
        <p className={styles.bounced}>{t("email.bouncedNote")}</p>
      ) : status === "unverified" ? (
        <p className={styles.note}>
          {t(neverSent ? "email.unsentNote" : "email.unverifiedNote", { email: user.email })}
        </p>
      ) : null}

      {message ? (
        // role="status": a polite announcement of something the reader asked for.
        <p className={styles.message} role="status">
          {message}
        </p>
      ) : null}

      {changing ? (
        <form className={styles.form} onSubmit={(e) => void submitChange(e)}>
          {/* A hidden username, so a password manager attaches the current
              password to THIS account rather than guessing (Settings' own
              change-password form does the same). */}
          {user.has_password ? (
            <input type="text" name="username" autoComplete="username" value={user.email} readOnly hidden />
          ) : null}

          <label className={styles.label} htmlFor="new-email">
            {t("email.newEmail")}
          </label>
          <input
            id="new-email"
            className={styles.input}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />

          {user.has_password ? (
            <>
              <label className={styles.label} htmlFor="change-email-password">
                {t("email.currentPassword")}
              </label>
              <input
                id="change-email-password"
                className={styles.input}
                type="password"
                name="current-password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </>
          ) : null}

          {changeError ? (
            <p className={styles.message} role="alert">
              {changeError}
            </p>
          ) : null}

          <div className={buttons}>
            <button className={styles.button} type="submit" disabled={busy}>
              {t("email.save")}
            </button>
            <button
              className={styles.button}
              type="button"
              onClick={() => {
                setChanging(false);
                setChangeError(null);
              }}
            >
              {t("email.cancel")}
            </button>
          </div>
        </form>
      ) : (
        <div className={buttons}>
          {pending ? (
            <>
              <button className={styles.button} type="button" disabled={busy} onClick={() => void resendChange()}>
                {t("email.resend")}
              </button>
              <button className={styles.button} type="button" disabled={busy} onClick={() => void cancelChange()}>
                {t("email.cancelChange")}
              </button>
            </>
          ) : (
            <>
              {status !== "verified" ? (
                <button
                  className={styles.button}
                  type="button"
                  disabled={busy}
                  onClick={() => void resendVerification()}
                >
                  {variant === "pending"
                    ? t(neverSent ? "email.send" : "email.resend")
                    : t(neverSent ? "email.sendVerification" : "email.resendVerification")}
                </button>
              ) : null}
              <button
                className={styles.button}
                type="button"
                onClick={() => {
                  setChanging(true);
                  setChangeError(null);
                  setMessage(null);
                }}
              >
                {t("email.change")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The re-send on its own, for a screen that needs the address verified for a
 * reason of its own (decision 622: Settings' email summaries, onboarding's
 * summaries step). The same call, button and sentences as the block above, so
 * "Re-send verification email" means one thing wherever it appears -- and
 * "Send" where no link was ever sent.
 */
export function ResendVerificationButton({ user }: { user: User }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // Held here rather than re-read: this button has no way to hand a fresh
  // account back to its screen, and once it has sent, a link is waiting.
  const [sent, setSent] = useState(user.email_verification_sent);

  async function resend() {
    setBusy(true);
    setMessage(null);
    try {
      await api.requestEmailVerification();
      setMessage(t(sent ? "email.verificationSent" : "email.firstVerificationSent", { email: user.email }));
      setSent(true);
    } catch (err) {
      setMessage(statusOf(err) === 429 ? t("email.cooldown") : t("email.sendFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.block}>
      <div className={styles.row}>
        <button className={styles.button} type="button" disabled={busy} onClick={() => void resend()}>
          {t(sent ? "email.resendVerification" : "email.sendVerification")}
        </button>
      </div>
      {message ? (
        <p className={styles.message} role="status">
          {message}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One of the block's actions: does its call and returns the sentence to show.
 * A call signature rather than an arrow type, because literals.test.ts reads
 * `=> Promise<...>` as text between JSX tags.
 */
interface EmailAction {
  (): Promise<string>;
}

/**
 * The HTTP status of a failed call, when it has one worth branching on. 429,
 * 400 and 502 all arrive as the client's `server` kind.
 */
function statusOf(err: unknown): number | null {
  if (err instanceof ApiError && err.failure.kind === "server") return err.failure.status;
  return null;
}
